/** @module api/reporters */

import { apiCall } from '@/api/index'
import { MEDIUM_FLAG } from '@/store/modules/reporters'
import {
  apiV1ReporterPath,
  apiV1ReportersPath,
  subscribeApiV1ReporterPath,
  subscribedApiV1ReportersPath,
  subscriptionApiV1ReporterPath,
  targetGroupApiV1ReportersPath,
  targetGroupsRelationshipEmssApiV1ReportersPath,
  unsubscribeApiV1ReporterPath
} from '@/../lib/routes'

const REPORTER_TYPE_MAP = {
  logEvent: 'Reporters::LogEventProber',
  sensor: 'Reporters::SensorProber'
}

/**
 * API GET to get a collection of Reporter(s) of a group
 *
 * @function
 *
 * @param {object} params
 * @param {string} params.ctxt is the context of the request
 * @param {integer} params.groupId
 *
 * @return {promise}
 */
export function getReportersForGroup({ ctxt = 'customer', groupId } = {}) {
  const payload = {}

  return apiCall({
    path: targetGroupApiV1ReportersPath(ctxt, groupId),
    payload
  })
}

/**
 * API GET to get a collection of Reporter(s) for the current User
 *
 * @function
 *
 * @param {object} params
 * @param {string} params.ctxt is the context of the request
 *
 * @return {promise}
 */
export function getReportersForCurrentUser({ ctxt = 'customer' } = {}) {
  const payload = {}

  return apiCall({
    path: subscribedApiV1ReportersPath(ctxt),
    payload
  })
}

/**
 * API GET to get a Reporter
 *
 * @function
 *
 * @param {object} params
 * @param {string} params.ctxt is the context of the request
 * @param {integer} params.reporterId
 *
 * @return {promise}
 */
export function getReporter({ ctxt = 'customer', reporterId } = {}) {
  const payload = {}

  return apiCall({
    path: apiV1ReporterPath(ctxt, reporterId),
    payload
  })
}

/**
 * API DELETE to delete a Reporter
 *
 * @function
 *
 * @param {object} params
 * @param {string} params.ctxt is the context of the request
 * @param {integer} params.reporterId
 *
 * @return {promise}
 */
export function deleteReporter({ ctxt = 'customer', reporterId } = {}) {
  return apiCall({
    method: 'DELETE',
    path: apiV1ReporterPath(ctxt, reporterId)
  })
}

/**
 * API PATCH to update an existing Reporter
 *
 * @function
 *
 * @param {object} params
 * @param {string} params.ctxt is the context of the request
 * @param {object}Reporter params.reporter
 *
 * @return {promise}
 */
export function updateReporter({ ctxt = 'customer', reporter } = {}) {
  const users = []
  for (const userId in reporter.users) {
    const user = {
      type: 'users',
      id: userId.toString(),
      meta: {}
    }
    let medium = 0
    for (const mediumType in MEDIUM_FLAG) {
      if (!reporter.users[userId][mediumType]) continue

      medium |= MEDIUM_FLAG[mediumType]
    }
    user.meta.medium = medium
    users.push(user)
  }

  const payload = {
    data: {
      type: 'reporters',
      attributes: {
        config: reporter.config,
        disabled: reporter.disabled,
        name: reporter.name,
        notifyOnConditionNoLongerFulfilled: reporter.notifyOnConditionNoLongerFulfilled,
        type: REPORTER_TYPE_MAP[reporter.type]
      },
      relationships: {
        group: {
          data: {
            type: 'groups',
            id: reporter.groupId.toString()
          }
        },
        users: {
          data: users
        }
      }
    }
  }

  return apiCall({
    method: 'PATCH',
    path: apiV1ReporterPath(ctxt, reporter.id),
    payload
  })
}

/**
 * API POST to create a Reporter
 *
 * @function
 *
 * @param {object} params
 * @param {string} params.ctxt is the context of the request
 * @param {object}Reporter params.reporter
 *
 * @return {promise}
 */
export function createReporter({ ctxt = 'customer', reporter } = {}) {
  const users = []
  for (const userId in reporter.users) {
    const user = {
      type: 'users',
      id: userId.toString(),
      meta: {}
    }
    let medium = 0
    for (const mediumType in MEDIUM_FLAG) {
      if (!reporter.users[userId][mediumType]) continue

      medium |= MEDIUM_FLAG[mediumType]
    }
    user.meta.medium = medium
    users.push(user)
  }

  const payload = {
    data: {
      type: 'reporters',
      attributes: {
        config: reporter.config,
        disabled: reporter.disabled,
        name: reporter.name,
        notifyOnConditionNoLongerFulfilled: reporter.notifyOnConditionNoLongerFulfilled,
        type: REPORTER_TYPE_MAP[reporter.type]
      },
      relationships: {
        group: {
          data: {
            type: 'groups',
            id: reporter.groupId.toString()
          }
        },
        users: {
          data: users
        }
      }
    }
  }

  return apiCall({
    method: 'POST',
    path: apiV1ReportersPath(ctxt),
    payload
  })
}

/**
 * API PATCH to change the medium of a user in a reporter
 *
 * @function
 *
 * @param {object} params
 * @param {string} params.ctxt is the context of the request
 * @param {integer} params.reporterId,
 * @param {integer} params.mediumVal
 *
 * @return {promise}
 */
export function changeMedium({ ctxt = 'customer', reporterId, mediumVal }) {
  const payload = {
    data: {
      type: 'reporter_subscriptions',
      attributes: {
        medium: mediumVal
      }
    }
  }
  return apiCall({
    method: 'PATCH',
    path: subscriptionApiV1ReporterPath(ctxt, reporterId),
    payload
  })
}

/**
 * API PATCH to add the current user to a reporter
 *
 * @function
 *
 * @param {object} params
 * @param {string} params.ctxt is the context of the request
 * @param {integer} params.reporterId
 *
 * @return {promise}
 */
export function subscribe({ ctxt = 'customer', reporterId } = {}) {
  return apiCall({
    method: 'PATCH',
    path: subscribeApiV1ReporterPath(ctxt, reporterId)
  })
}

/**
 * API PATCH to remove the current user from a reporter
 *
 * @function
 *
 * @param {object} params
 * @param {string} params.ctxt is the context of the request
 * @param {integer} params.reporterId
 *
 * @return {promise}
 */
export function unsubscribe({ ctxt = 'customer', reporterId } = {}) {
  return apiCall({
    method: 'PATCH',
    path: unsubscribeApiV1ReporterPath(ctxt, reporterId)
  })
}

/**
 * API GET to get all EMSs for reporter Groups
 *
 * @function
 *
 * @param {object} params
 * @param {string} params.ctxt is the context of the request
 * @param Array{integer} params.groupIds
 *
 * @return {promise}
 */
export function getEmssForReporterGroups({ ctxt = 'customer', groupIds }) {
  const payload = {
    filter: {
      groups: {
        id: groupIds
      }
    }
  }

  return apiCall({
    path: targetGroupsRelationshipEmssApiV1ReportersPath(ctxt),
    payload
  })
}
